import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { organizationAccessTokenContract } from "@common/contracts/organization-access-token.contract";
import {
    GetOrganizationAccessTokenResponseSchema,
    OrganizationWebhookPortalResponseSchema,
    RotateOrganizationAccessTokenResponseSchema,
    type OrganizationAccessToken,
    type RotateOrganizationAccessTokenResponse,
} from "@common/schemas/domains/organizationAccessToken/organization-access-token.schema";

const organizationAccessTokenClient = getBackendAPIClient(
    organizationAccessTokenContract,
);

const organizationAccessTokenQueryKeys = {
    all: (organizationId: string) =>
        ["organization-access-token", organizationId] as const,
};

export function useOrganizationAccessToken(organizationId: string) {
    return useQuery({
        queryKey: organizationAccessTokenQueryKeys.all(organizationId),
        queryFn: async (): Promise<OrganizationAccessToken | null> => {
            const result = await organizationAccessTokenClient.get({
                params: { organizationId },
            });
            return GetOrganizationAccessTokenResponseSchema.parse(result.body)
                .token;
        },
        enabled: Boolean(organizationId),
    });
}

export function useRotateOrganizationAccessTokenMutation(
    organizationId: string,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn:
            async (): Promise<RotateOrganizationAccessTokenResponse> => {
                const result = await organizationAccessTokenClient.rotate({
                    params: { organizationId },
                });
                return RotateOrganizationAccessTokenResponseSchema.parse(
                    result.body,
                );
            },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: organizationAccessTokenQueryKeys.all(organizationId),
            });
        },
    });
}

export function useOrganizationWebhookPortalMutation(organizationId: string) {
    return useMutation({
        mutationFn: async (): Promise<string> => {
            const result = await organizationAccessTokenClient.webhookPortal({
                params: { organizationId },
            });
            return OrganizationWebhookPortalResponseSchema.parse(result.body)
                .url;
        },
    });
}
